import type { ComponentType, SVGProps } from 'react';
import { memo } from 'react';

import classNames from 'classnames';

import type { TypographyColors } from '@ui/uikit/components/common/Typography';
import type { ImageProps } from '@utilities/Image';
import { Image } from '@utilities/Image';

import styles from './BrandIcon.module.scss';

type BrandIconSize = 'extra-small' | 'small' | 'medium' | 'large';

type BrandIconBaseProps = {
  label?: string;
  labelColor?: TypographyColors;
  size?: BrandIconSize;
  strong?: boolean;
  dataQa?: string;
};

export type BrandIconProps = BrandIconBaseProps &
  (
    | {
        icon: ComponentType<SVGProps<SVGSVGElement>>;
        iconColor?: TypographyColors;
        src?: never;
        loading?: never;
      }
    | {
        src: ImageProps['src'];
        loading?: ImageProps['loading'];
        icon?: never;
        iconColor?: never;
      }
  );

export const BrandIcon = memo(
  ({
    label = '',
    size = 'medium',
    iconColor,
    src,
    loading,
    dataQa,
    strong = false,
    labelColor = 'primary',
    ...rest
  }: BrandIconProps) => {
    const Icon = rest?.icon;

    const icon = Icon ? (
      <Icon
        className={classNames(styles.icon, styles[size])}
        style={{ color: `var(--text-color-${iconColor})` }}
      />
    ) : null;

    const image = src ? (
      <Image
        className={classNames(styles.icon, styles[size])}
        alt={label}
        src={src}
        loading={loading}
        // set the maximum available size and adjust it via css
        width={32}
        height={32}
      />
    ) : null;

    return (
      <div data-qa={dataQa} className={classNames(styles.wrapper, styles[size], { [styles.strong]: strong })} style={{ color: `var(--text-color-${labelColor})` }}>
        {icon || image}
        {label}
      </div>
    );
  },
);
